import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import {
  Loader2, ShieldAlert, ArrowLeft, FlaskConical, Play, Calculator,
  UploadCloud, X, Save, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { getIsAdmin } from "@/lib/reports.functions";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { JPY_RATE, formatUSD } from "@/lib/modelPricing";
import {
  MODEL_BENCHES, PROVIDER_LABEL, groupEstimatedUsd, median, percentile,
  type BenchItem, type ImageBenchItem, type ModelBench, type VideoBenchItem,
} from "@/lib/labBench";

export const Route = createFileRoute("/_authenticated/admin_/lab-runs")({
  head: () => ({
    meta: [
      { title: "Lab コスト分析 (Admin) | KAGEM" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: LabRunsPage,
});

interface LabRun {
  id: string;
  created_at: string;
  kind: "image" | "video";
  model: string;
  prompt: string | null;
  duration_sec: number | null;
  aspect_ratio: string | null;
  ref_image_count: number;
  cost_usd: number;
  actual_cost_usd: number | null;
  elapsed_ms: number | null;
  status: "processing" | "completed" | "failed";
  output_url: string | null;
  error_message: string | null;
  bench_tag: string | null;
  external_job_id: string | null;
}

interface BenchMaterials {
  prompt_text: string;
  prompt_edit: string;
  talking_text: string;
  image_url: string;
  video_url: string;
}


const DEFAULT_MATERIALS: BenchMaterials = {
  prompt_text: "A cinematic portrait of a Japanese woman in soft window light, 35mm film look",
  prompt_edit: "Change the background to a neon-lit Tokyo street at night",
  talking_text: "こんにちは、KAGEM のテストです。",
  image_url: "",
  video_url: "",
};

function LabRunsPage() {
  const isAdminFn = useServerFn(getIsAdmin);
  const [checked, setChecked] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [rows, setRows] = useState<LabRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [materials, setMaterials] = useState<BenchMaterials>(DEFAULT_MATERIALS);

  useEffect(() => {
    (async () => {
      try {
        const { data: s } = await supabase.auth.getSession();
        if (!s.session) return;
        const r = await isAdminFn({
          headers: { Authorization: `Bearer ${s.session.access_token}` },
        });
        setIsAdmin(r.isAdmin);
        if (r.isAdmin) {
          await Promise.all([loadRuns(), loadMaterials()]);
        }
      } finally {
        setChecked(true);
      }
    })();
  }, [isAdminFn]);

  // 処理中のジョブを自動ポーリング (Runway / Kling)
  useEffect(() => {
    const processing = rows.filter(
      (r) => r.status === "processing" && r.kind === "video" &&
        (r.model.startsWith("runway/") || r.model.startsWith("kling/")),
    );
    if (processing.length === 0) return;
    let cancelled = false;
    const tick = async () => {
      const { data: s } = await supabase.auth.getSession();
      const token = s.session?.access_token;
      if (!token) return;
      let updated = false;
      for (const r of processing) {
        if (cancelled) return;
        if (!r.external_job_id) continue;
        const action = r.model.startsWith("runway/") ? "poll-runway" : "poll-kling";
        try {
          const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-lab-video`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({ action, external_job_id: r.external_job_id, model_id: r.model }),
          });
          const j = await res.json().catch(() => ({}));
          if (j?.status === "completed" || j?.status === "failed") updated = true;
        } catch (e) { console.warn("poll fail", r.id, e); }
      }
      if (updated && !cancelled) await loadRuns();
    };
    void tick();
    const id = setInterval(tick, 8000);
    return () => { cancelled = true; clearInterval(id); };
  }, [rows]);

  async function loadRuns() {
    setLoading(true);
    const { data } = await supabase
      .from("lab_runs")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(1000);
    if (data) setRows(data as unknown as LabRun[]);
    setLoading(false);
  }

  async function loadMaterials() {
    const { data } = await supabase
      .from("system_meta")
      .select("value")
      .eq("key", "lab_bench_materials")
      .maybeSingle();
    if (data?.value) setMaterials({ ...DEFAULT_MATERIALS, ...(data.value as Partial<BenchMaterials>) });
  }

  if (!checked) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </main>
    );
  }
  if (!isAdmin) {
    return (
      <main className="min-h-screen bg-background px-4 py-16">
        <div className="mx-auto max-w-md">
          <Card className="p-8 text-center">
            <ShieldAlert className="mx-auto mb-4 h-10 w-10 text-destructive" />
            <h1 className="text-xl font-bold">アクセス権がありません</h1>
            <Button asChild className="mt-6"><Link to="/dashboard">ダッシュボードへ</Link></Button>
          </Card>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background px-4 py-10">
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
              <FlaskConical className="h-7 w-7 text-primary" />
              Lab コスト分析
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              ベンチマーク自動実行 → 実コスト集計 → クレジット単価逆算
            </p>
          </div>
          <Button asChild variant="outline" size="sm">
            <Link to="/admin"><ArrowLeft className="mr-2 h-4 w-4" />管理画面へ</Link>
          </Button>
        </div>

        <MaterialsSection materials={materials} setMaterials={setMaterials} />

        <RunnerSection materials={materials} onRefresh={loadRuns} />

        <AggregationSection rows={rows} loading={loading} onReload={loadRuns} />

        <SimulatorSection rows={rows} />

        <DetailSection rows={rows} onRowUpdated={(id, actual) => {
          setRows((prev) => prev.map((r) => r.id === id ? { ...r, actual_cost_usd: actual } : r));
        }} />
      </div>
    </main>
  );
}

/* ============ 1. ベンチ素材 ============ */

function MaterialsSection({
  materials, setMaterials,
}: {
  materials: BenchMaterials;
  setMaterials: (m: BenchMaterials) => void;
}) {
  const [saving, setSaving] = useState(false);

  async function uploadFile(file: File, kind: "image" | "video"): Promise<string> {
    const { data: s } = await supabase.auth.getSession();
    const userId = s.session?.user.id;
    if (!userId) throw new Error("not signed in");
    const ext = file.name.split(".").pop() ?? (kind === "image" ? "jpg" : "mp4");
    const path = `${userId}/lab-bench/${crypto.randomUUID()}.${ext}`;
    const { error } = await supabase.storage.from("uploads").upload(path, file);
    if (error) throw error;
    const { data: signed } = await supabase.storage
      .from("uploads")
      .createSignedUrl(path, 60 * 60 * 24 * 7);
    return signed?.signedUrl ?? "";
  }

  async function save() {
    setSaving(true);
    try {
      const { error } = await supabase
        .from("system_meta")
        .upsert(
          [{ key: "lab_bench_materials", value: materials as unknown as never }],
          { onConflict: "key" },
        );
      if (error) throw error;
      toast.success("素材を保存しました");
    } catch (e) {
      toast.error(String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card className="p-5">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-lg font-semibold">① ベンチ素材（全テストで使い回す）</h2>
        <Button size="sm" onClick={save} disabled={saving}>
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
          保存
        </Button>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-3">
          <div>
            <Label className="text-xs">テキストプロンプト（生成用・英文推奨）</Label>
            <Textarea
              rows={2}
              value={materials.prompt_text}
              onChange={(e) => setMaterials({ ...materials, prompt_text: e.target.value })}
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-xs">編集プロンプト（i2i 用）</Label>
            <Textarea
              rows={2}
              value={materials.prompt_edit}
              onChange={(e) => setMaterials({ ...materials, prompt_edit: e.target.value })}
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-xs">HeyGen 用テキスト（喋らせる文）</Label>
            <Textarea
              rows={2}
              value={materials.talking_text}
              onChange={(e) => setMaterials({ ...materials, talking_text: e.target.value })}
              className="mt-1"
            />
          </div>
        </div>
        <div className="space-y-3">
          <FileSlot
            label="人物写真（i2i / 動画 image-to-video / HeyGen 用）"
            url={materials.image_url}
            onUpload={async (f) => {
              toast.info("アップロード中…");
              try {
                const u = await uploadFile(f, "image");
                setMaterials({ ...materials, image_url: u });
                toast.success("OK");
              } catch (e) { toast.error(String(e)); }
            }}
            onClear={() => setMaterials({ ...materials, image_url: "" })}
            accept="image/*"
            kind="image"
          />
          <FileSlot
            label="参照動画 5〜10s（Kling Omni 用）"
            url={materials.video_url}
            onUpload={async (f) => {
              toast.info("アップロード中…");
              try {
                const u = await uploadFile(f, "video");
                setMaterials({ ...materials, video_url: u });
                toast.success("OK");
              } catch (e) { toast.error(String(e)); }
            }}
            onClear={() => setMaterials({ ...materials, video_url: "" })}
            accept="video/*"
            kind="video"
          />
        </div>
      </div>
    </Card>
  );
}

function FileSlot({
  label, url, onUpload, onClear, accept, kind,
}: {
  label: string; url: string;
  onUpload: (f: File) => void | Promise<void>;
  onClear: () => void;
  accept: string; kind: "image" | "video";
}) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <div className="mt-1 rounded-md border bg-muted/30 p-2">
        {url ? (
          <div className="flex items-center gap-3">
            {kind === "image"
              ? <img src={url} alt="" className="h-16 w-16 rounded object-cover" />
              : <video src={url} className="h-16 w-24 rounded object-cover" muted />}
            <div className="flex-1 truncate text-[10px] text-muted-foreground">{url}</div>
            <Button size="icon" variant="ghost" onClick={onClear}><X className="h-4 w-4" /></Button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => ref.current?.click()}
            className="flex w-full items-center justify-center gap-2 rounded py-4 text-xs text-muted-foreground hover:bg-muted"
          >
            <UploadCloud className="h-4 w-4" />
            アップロード
          </button>
        )}
        <input
          ref={ref}
          type="file"
          accept={accept}
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void onUpload(f);
            e.target.value = "";
          }}
        />
      </div>
    </div>
  );
}

/* ============ 2. ベンチマーク実行ランナー ============ */

interface RunnerProgress {
  groupId: string | null;
  done: number;
  total: number;
  failed: number;
}

function RunnerSection({
  materials, onRefresh,
}: {
  materials: BenchMaterials;
  onRefresh: () => Promise<void>;
}) {
  const [progress, setProgress] = useState<RunnerProgress>({ groupId: null, done: 0, total: 0, failed: 0 });
  const cancelRef = useRef(false);

  async function runBench(benchId: string, items: BenchItem[]) {
    if (progress.groupId) {
      toast.error("他のベンチが実行中です");
      return;
    }
    cancelRef.current = false;
    setProgress({ groupId: benchId, done: 0, total: items.length, failed: 0 });

    const { data: s } = await supabase.auth.getSession();
    const token = s.session?.access_token;
    if (!token) {
      toast.error("未ログイン");
      setProgress({ groupId: null, done: 0, total: 0, failed: 0 });
      return;
    }

    let done = 0, failed = 0;
    for (const item of items) {
      if (cancelRef.current) break;
      try {
        if (item.kind === "image") {
          await runImageItem(item, materials, token);
        } else {
          await runVideoItem(item, materials, token);
        }
      } catch (e) {
        console.error("bench item failed", item.bench_tag, e);
        failed++;
      }
      done++;
      setProgress({ groupId: benchId, done, total: items.length, failed });
    }
    toast.success(`完了: ${done}/${items.length} (失敗 ${failed})`);
    setProgress({ groupId: null, done: 0, total: 0, failed: 0 });
    await onRefresh();
  }

  // provider ごとにグルーピングして表示
  const byProvider = useMemo(() => {
    const map = new Map<ModelBench["provider"], ModelBench[]>();
    for (const b of MODEL_BENCHES) {
      if (!map.has(b.provider)) map.set(b.provider, []);
      map.get(b.provider)!.push(b);
    }
    return Array.from(map.entries());
  }, []);

  return (
    <Card className="p-5">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-lg font-semibold">② ベンチマーク自動実行（モデル単位）</h2>
        {progress.groupId && (
          <Button size="sm" variant="destructive" onClick={() => { cancelRef.current = true; }}>
            キャンセル
          </Button>
        )}
      </div>
      <p className="mb-3 text-[11px] text-muted-foreground">
        1モデルずつ実行してください。実行前後で対応する公式ダッシュボードの残高/使用額をメモ → 差分が
        そのモデルの実コスト合計です。⑤で1件ずつ実測USDを入力すると ③④に反映されます。
      </p>
      <div className="space-y-4">
        {byProvider.map(([prov, benches]) => (
          <div key={prov}>
            <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {PROVIDER_LABEL[prov]}
            </div>
            <div className="space-y-2">
              {benches.map((b) => {
                const usd = groupEstimatedUsd(b.items);
                const yen = (usd * JPY_RATE).toFixed(0);
                const isRunning = progress.groupId === b.id;
                return (
                  <div key={b.id} className="flex items-center gap-3 rounded border p-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{b.label}</p>
                      <p className="text-xs text-muted-foreground">
                        {b.detail} · {b.items.length}件 · 推定 {formatUSD(usd)} (約 ¥{yen})
                      </p>
                      {isRunning && (
                        <Progress value={(progress.done / progress.total) * 100} className="mt-2 h-1.5" />
                      )}
                    </div>
                    <Button
                      size="sm"
                      disabled={!!progress.groupId}
                      onClick={() => runBench(b.id, b.items)}
                    >
                      {isRunning ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          {progress.done}/{progress.total}
                        </>
                      ) : (
                        <><Play className="mr-2 h-4 w-4" />実行</>
                      )}
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <p className="mt-3 text-[11px] text-muted-foreground">
        ※ 動画は submit のみ → 完了は Webhook / ポーリングで反映。Runway は AI Lab ページを
        開いておくか、再読込で取得し直してください。
      </p>
    </Card>
  );
}

async function runImageItem(item: ImageBenchItem, m: BenchMaterials, token: string) {
  const inputs: string[] = [];
  if (item.use_ref_image) {
    if (!m.image_url) throw new Error("image_url not set (① で人物写真をアップロードしてください)");
    inputs.push(m.image_url);
  }
  const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-lab-image`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      model: item.model,
      prompt: item.use_ref_image ? m.prompt_edit : m.prompt_text,
      aspect_ratio: item.aspect_ratio,
      input_image_urls: inputs.length ? inputs : undefined,
      bench_tag: item.bench_tag,
    }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(`HTTP ${res.status} ${json?.error ?? ""}`);
  if (json?.ok === false) throw new Error(`${json.error ?? "FAILED"}: ${json.detail ?? ""}`);
}

async function runVideoItem(item: VideoBenchItem, m: BenchMaterials, token: string) {
  if (item.needs_image && !m.image_url) throw new Error("image_url not set");
  if (item.needs_video && !m.video_url) throw new Error("video_url not set");
  const promptValue =
    item.model_id === "heygen/talking-photo" ? m.talking_text : m.prompt_text;
  const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-lab-video`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      action: "submit",
      model_id: item.model_id,
      input_image_url: item.needs_image ? m.image_url : undefined,
      input_video_url: item.needs_video ? m.video_url : undefined,
      prompt: item.needs_text ? promptValue : undefined,
      duration: item.duration,
      bench_tag: item.bench_tag,
    }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

/* ============ 3. モデル別 単価集計 ============ */

interface AggRow {
  key: string;
  model: string;
  unit: string;
  n: number;
  succeeded: number;
  failed: number;
  avgUsd: number;
  medianUsd: number;
  p95Usd: number;
  avgElapsedMs: number;
}

function aggregate(rows: LabRun[]): AggRow[] {
  // group key = (model, duration_sec ?? aspect_ratio ?? '-')
  const map = new Map<string, LabRun[]>();
  for (const r of rows) {
    const sub = r.kind === "video" ? `${r.duration_sec ?? "-"}s` : `${r.aspect_ratio ?? "-"}`;
    const key = `${r.model}::${sub}`;
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(r);
  }
  const out: AggRow[] = [];
  for (const [key, items] of map.entries()) {
    const [model, sub] = key.split("::");
    const ok = items.filter((x) => x.status === "completed");
    const failed = items.filter((x) => x.status === "failed").length;
    // 実測値 (actual_cost_usd) があればそれを優先、なければ推測 cost_usd
    const costs = ok
      .map((x) => Number(x.actual_cost_usd ?? x.cost_usd))
      .filter((n) => n > 0);
    const elapsed = ok.map((x) => Number(x.elapsed_ms ?? 0)).filter((n) => n > 0);
    out.push({
      key,
      model,
      unit: sub,
      n: items.length,
      succeeded: ok.length,
      failed,
      avgUsd: costs.length ? costs.reduce((a, b) => a + b, 0) / costs.length : 0,
      medianUsd: median(costs),
      p95Usd: percentile(costs, 95),
      avgElapsedMs: elapsed.length ? elapsed.reduce((a, b) => a + b, 0) / elapsed.length : 0,
    });
  }
  return out.sort((a, b) => (a.model < b.model ? -1 : a.model > b.model ? 1 : a.unit < b.unit ? -1 : 1));
}

function AggregationSection({
  rows, loading, onReload,
}: { rows: LabRun[]; loading: boolean; onReload: () => void }) {
  const aggs = useMemo(() => aggregate(rows), [rows]);
  return (
    <Card className="p-5">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-lg font-semibold">③ モデル別 単価（実測）</h2>
        <Button size="sm" variant="outline" onClick={onReload} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "再集計"}
        </Button>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>モデル</TableHead>
              <TableHead>単位</TableHead>
              <TableHead className="text-right">n (成功/失敗)</TableHead>
              <TableHead className="text-right">平均 USD</TableHead>
              <TableHead className="text-right">中央値</TableHead>
              <TableHead className="text-right">95%ile</TableHead>
              <TableHead className="text-right">平均 ¥</TableHead>
              <TableHead className="text-right">平均所要</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {aggs.map((a) => (
              <TableRow key={a.key}>
                <TableCell className="font-mono text-xs">{a.model}</TableCell>
                <TableCell className="text-xs">{a.unit}</TableCell>
                <TableCell className="text-right text-xs">
                  {a.succeeded}/{a.failed}
                </TableCell>
                <TableCell className="text-right text-xs font-bold">{formatUSD(a.avgUsd)}</TableCell>
                <TableCell className="text-right text-xs">{formatUSD(a.medianUsd)}</TableCell>
                <TableCell className="text-right text-xs">{formatUSD(a.p95Usd)}</TableCell>
                <TableCell className="text-right text-xs">¥{(a.avgUsd * JPY_RATE).toFixed(2)}</TableCell>
                <TableCell className="text-right text-xs">
                  {a.avgElapsedMs ? `${(a.avgElapsedMs / 1000).toFixed(1)}s` : "—"}
                </TableCell>
              </TableRow>
            ))}
            {aggs.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-sm text-muted-foreground py-8">
                  記録なし — まずベンチを実行してください
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}

/* ============ 4. クレジット単価シミュレータ ============ */

function SimulatorSection({ rows }: { rows: LabRun[] }) {
  const [yenPerCredit, setYenPerCredit] = useState(10);
  const [marginPct, setMarginPct] = useState(60);
  const [jpyRate, setJpyRate] = useState(JPY_RATE);

  const aggs = useMemo(() => aggregate(rows), [rows]);

  return (
    <Card className="p-5">
      <h2 className="mb-3 text-lg font-semibold flex items-center gap-2">
        <Calculator className="h-5 w-5 text-primary" />
        ④ クレジット単価シミュレータ
      </h2>
      <div className="mb-4 grid gap-3 md:grid-cols-3">
        <div>
          <Label className="text-xs">¥ / 1クレジット</Label>
          <Input
            type="number"
            value={yenPerCredit}
            onChange={(e) => setYenPerCredit(Number(e.target.value) || 0)}
            className="mt-1"
          />
        </div>
        <div>
          <Label className="text-xs">目標粗利率 %</Label>
          <Input
            type="number"
            value={marginPct}
            onChange={(e) => setMarginPct(Number(e.target.value) || 0)}
            className="mt-1"
          />
        </div>
        <div>
          <Label className="text-xs">JPY / USD レート</Label>
          <Input
            type="number"
            value={jpyRate}
            onChange={(e) => setJpyRate(Number(e.target.value) || 0)}
            className="mt-1"
          />
        </div>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>モデル</TableHead>
              <TableHead>単位</TableHead>
              <TableHead className="text-right">原価 ¥</TableHead>
              <TableHead className="text-right">必要売価 ¥</TableHead>
              <TableHead className="text-right">推奨クレジット</TableHead>
              <TableHead className="text-right">実利益率</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {aggs.map((a) => {
              const costYen = a.avgUsd * jpyRate;
              const requiredYen = marginPct >= 100 ? Infinity : costYen / (1 - marginPct / 100);
              const credits = yenPerCredit > 0 ? Math.max(1, Math.ceil(requiredYen / yenPerCredit)) : 0;
              const realRevenue = credits * yenPerCredit;
              const realMargin = realRevenue > 0 ? ((realRevenue - costYen) / realRevenue) * 100 : 0;
              return (
                <TableRow key={a.key}>
                  <TableCell className="font-mono text-xs">{a.model}</TableCell>
                  <TableCell className="text-xs">{a.unit}</TableCell>
                  <TableCell className="text-right text-xs">¥{costYen.toFixed(2)}</TableCell>
                  <TableCell className="text-right text-xs">¥{Number.isFinite(requiredYen) ? requiredYen.toFixed(2) : "∞"}</TableCell>
                  <TableCell className="text-right">
                    <Badge variant="default" className="font-bold">{credits} cr</Badge>
                  </TableCell>
                  <TableCell className={`text-right text-xs ${realMargin >= marginPct ? "text-emerald-600" : "text-destructive"}`}>
                    {realMargin.toFixed(1)}%
                  </TableCell>
                </TableRow>
              );
            })}
            {aggs.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-6">
                  集計データなし
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <p className="mt-3 text-[11px] text-muted-foreground">
        販売単価とマージンを変えるとリアルタイムで「必要クレジット数」が逆算されます。
        この表をそのまま <code>imageProviders.ts</code> / <code>modelPricing.ts</code> /
        プラン定義に反映してください。
      </p>
    </Card>
  );
}

/* ============ 5. 詳細履歴（実行ごと 実測値入力） ============ */

function DetailSection({
  rows,
  onRowUpdated,
}: {
  rows: LabRun[];
  onRowUpdated: (id: string, actual: number | null) => void;
}) {
  const [modelFilter, setModelFilter] = useState<string>("all");
  const models = useMemo(
    () => Array.from(new Set(rows.map((r) => r.model))).sort(),
    [rows],
  );
  const filtered = useMemo(
    () => (modelFilter === "all" ? rows : rows.filter((r) => r.model === modelFilter)),
    [rows, modelFilter],
  );

  return (
    <Card className="p-5">
      <h2 className="mb-1 text-lg font-semibold">⑤ 実行ごと 実測USD入力</h2>
      <p className="mb-3 text-xs text-muted-foreground">
        各実行の <strong>実測USD</strong> セルに公式ダッシュボードで確認した消費額を入力 → Enter / フォーカス外で保存。
        入力値は ③ 単価集計 と ④ シミュレータに即時反映されます。
      </p>
      <div className="mb-3 flex flex-wrap gap-2 text-xs">
        <span className="text-muted-foreground self-center mr-1">ダッシュボード:</span>
        {[
          { label: "Lovable AI (Image)", url: "https://lovable.dev/settings/workspace" },
          { label: "Runway", url: "https://app.runwayml.com/account/billing" },
          { label: "Kling (Kie.ai)", url: "https://kie.ai/billing" },
          { label: "HeyGen", url: "https://app.heygen.com/settings?nav=Subscriptions" },
        ].map((p) => (
          <a
            key={p.label}
            href={p.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 hover:bg-muted"
          >
            {p.label}
            <ExternalLink className="h-3 w-3" />
          </a>
        ))}
      </div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="text-xs text-muted-foreground">モデル絞り込み:</span>
        <button
          type="button"
          onClick={() => setModelFilter("all")}
          className={`rounded border px-2 py-0.5 text-[11px] ${modelFilter === "all" ? "bg-primary text-primary-foreground border-primary" : "hover:bg-muted"}`}
        >
          すべて ({rows.length})
        </button>
        {models.map((m) => {
          const n = rows.filter((r) => r.model === m).length;
          const active = modelFilter === m;
          return (
            <button
              key={m}
              type="button"
              onClick={() => setModelFilter(m)}
              className={`rounded border px-2 py-0.5 font-mono text-[10px] ${active ? "bg-primary text-primary-foreground border-primary" : "hover:bg-muted"}`}
            >
              {m} ({n})
            </button>
          );
        })}
      </div>
      <div className="max-h-[600px] overflow-auto">
        <Table>
          <TableHeader className="sticky top-0 bg-background z-10">
            <TableRow>
              <TableHead className="w-[64px]">出力</TableHead>
              <TableHead>日時</TableHead>
              <TableHead>tag</TableHead>
              <TableHead>モデル</TableHead>
              <TableHead className="text-right">秒/比</TableHead>
              <TableHead className="text-right">推定USD</TableHead>
              <TableHead className="text-right w-[120px]">実測USD</TableHead>
              <TableHead className="text-right">差分</TableHead>
              <TableHead>状態</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.slice(0, 500).map((r) => (
              <ActualRow key={r.id} row={r} onUpdated={onRowUpdated} />
            ))}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} className="text-center text-sm text-muted-foreground py-6">
                  記録なし
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}

function ActualRow({
  row,
  onUpdated,
}: {
  row: LabRun;
  onUpdated: (id: string, actual: number | null) => void;
}) {
  const initial = row.actual_cost_usd != null ? String(row.actual_cost_usd) : "";
  const [val, setVal] = useState(initial);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setVal(row.actual_cost_usd != null ? String(row.actual_cost_usd) : "");
  }, [row.actual_cost_usd]);

  async function commit() {
    const trimmed = val.trim();
    const next = trimmed === "" ? null : Number(trimmed);
    if (next != null && !Number.isFinite(next)) {
      toast.error("数値が不正");
      return;
    }
    const prev = row.actual_cost_usd ?? null;
    if (prev === next) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from("lab_runs")
        .update({ actual_cost_usd: next })
        .eq("id", row.id);
      if (error) throw error;
      onUpdated(row.id, next);
      toast.success("保存しました", { duration: 1200 });
    } catch (e) {
      toast.error(String(e));
      setVal(prev != null ? String(prev) : "");
    } finally {
      setSaving(false);
    }
  }

  const est = Number(row.cost_usd);
  const act = row.actual_cost_usd != null ? Number(row.actual_cost_usd) : null;
  const diff = act != null ? act - est : null;

  return (
    <TableRow>
      <TableCell>
        {row.output_url ? (
          row.kind === "image" ? (
            <a href={row.output_url} target="_blank" rel="noopener noreferrer" title="原寸を新規タブで開く">
              <img
                src={row.output_url}
                alt=""
                loading="lazy"
                className="h-12 w-12 rounded object-cover border bg-muted"
              />
            </a>
          ) : (
            <video
              src={row.output_url}
              className="h-12 w-20 rounded object-cover border bg-muted"
              muted
              playsInline
              controls
              preload="metadata"
            />
          )
        ) : row.status === "failed" ? (
          <span title={row.error_message ?? ""} className="text-[10px] text-destructive">エラー</span>
        ) : (
          <span className="text-[10px] text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell className="whitespace-nowrap text-[11px]">
        {new Date(row.created_at).toLocaleString("ja-JP", {
          month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
        })}
      </TableCell>
      <TableCell className="font-mono text-[10px]">{row.bench_tag ?? "—"}</TableCell>
      <TableCell className="font-mono text-[11px]">{row.model}</TableCell>
      <TableCell className="text-right text-[11px]">
        {row.duration_sec ?? row.aspect_ratio ?? "—"}
      </TableCell>
      <TableCell className="text-right text-[11px]">{formatUSD(est)}</TableCell>
      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-1">
          <Input
            type="number"
            step="0.0001"
            value={val}
            onChange={(e) => setVal(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === "Enter") (e.target as HTMLInputElement).blur();
            }}
            className="h-7 w-[90px] text-right text-xs"
            disabled={saving || row.status !== "completed"}
            placeholder="—"
          />
          {saving && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
        </div>
      </TableCell>
      <TableCell
        className={`text-right text-[11px] font-medium ${
          diff == null
            ? "text-muted-foreground"
            : diff > 0
              ? "text-destructive"
              : diff < 0
                ? "text-emerald-600"
                : ""
        }`}
      >
        {diff != null ? `${diff >= 0 ? "+" : ""}${formatUSD(diff)}` : "—"}
      </TableCell>
      <TableCell>
        <Badge
          variant={row.status === "completed" ? "default" : row.status === "failed" ? "destructive" : "outline"}
        >
          {row.status}
        </Badge>
      </TableCell>
    </TableRow>
  );
}